import styled from "@emotion/styled";
import { uniqBy } from "lodash";
import { memo, useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { Device, NetworkSelected, NetworkType } from "types";
import DashboardCreateModal, { DashboardCreateModalProps } from ".";
import { useCreateDashboardStore } from "..";
import { isPSLDevice } from "utils/sensorFunctions";

const StyledDashboardCreateModal = styled(DashboardCreateModal)`
    .modal-dialog {
        width: 650px;
        max-width: 650px;
    }
    .modal-option .custom-control-label:before,
    .modal-option .custom-control-label:after {
        top: 50%;
        transform: translateY(-50%);
    }
    .modal-options {
        position: relative;
        min-height: 10vh;
        max-height: 50vh;
        overflow-y: auto;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
    }
    .modal-options::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: 768px) {
        .modal-dialog {
            width: unset !important;
            max-width: unset !important;
        }
    }
`;

const networkTypeOptions = [
    { label: "Room", value: NetworkType.ROOM },
    { label: "Zone", value: NetworkType.ZONE },
    { label: "Group", value: NetworkType.GROUP },
];

const DashboardCreateSelectNetworkModal: React.FunctionComponent<
    Omit<DashboardCreateModalProps, "onSave"> & {
        networkType?: NetworkType;
        filter?: (d: Device) => boolean;
        maxSelected?: number;
        selected: Record<string, NetworkSelected>;
        multipleSelection?: boolean;
        excludes?: string[];
        onSave: (event: any, data: Record<string, NetworkSelected>) => void;
    }
> = (props) => {

    const {
        show,
        onHide,
        onSave,
        maxSelected,
        selected,
        networkType,
        multipleSelection = true,
        excludes = [],
        filter,
    } = props;

    const [filteredList, setFilteredList] = useState<Device[]>([]);
    const [searchCondition, setSearchCondition] = useState<{
        name: string;
        type: string;
        networkId: string;
    }>({
        name: "",
        type: "",
        networkId: "",
    });
    const [values, setValues] = useState<Record<string, NetworkSelected>>(selected || {});

    const { deviceList, loading } = useCreateDashboardStore();
    const isSelectAll =
        filteredList.length > 0 &&
        filteredList.every((device) => device.deviceId in values);

    const uniqNetworkList = uniqBy(
        deviceList.filter(
            (network) =>
                isPSLDevice(network) &&
                (networkType
                    ? network.deviceType === networkType
                    : network.deviceType !== "hub")
        ),
        (n) => n.gatewayId
    );

    const displayedNetworkType =
        (networkType?.capitalize() || "Rooms/Zones/Group") + "s";

    const filterFunction = (network: Device) =>
        (!searchCondition.name ||
            network.deviceName?.toLowerCase()
                .includes(searchCondition.name.toLowerCase().trim())) &&
        (!searchCondition.networkId ||
            network.gatewayId
                .toLowerCase()
                .includes(searchCondition.networkId.toLowerCase())) &&
        (!searchCondition.type ||
            network.deviceType === searchCondition.type) &&
        (!networkType || network.deviceType === networkType);

    const getDeviceList = () => {

        return uniqBy(deviceList, (n) => n.ldsuUID + n.gatewayId)
            .filter(
                (n) =>
                    isPSLDevice(n) &&
                    (networkType
                        ? n.deviceType === networkType
                        : n.deviceType !== "hub") &&
                    !excludes.includes(n.deviceId)
            )
            .filter(filterFunction)
            .filter((n) => !filter || filter(n))
            .sort((a, b) =>
                a.deviceName?.localeCompare(b.deviceName || "") || 0
            );
    };

    useEffect(() => {
        if (show && deviceList.length > 0) {
            // Initialize with selected networks
            setValues(selected || {});
            setFilteredList(getDeviceList());
        }

        // Reset search condition when modal opens
        setSearchCondition({
            name: "",
            type: "",
            networkId: "",
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, deviceList]);

    useEffect(() => {
        setFilteredList(getDeviceList());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCondition]);

    const handleSelect = (
        e: React.ChangeEvent<HTMLInputElement>,
        network: Device
    ) => {
        const checked = e.target.checked;
        const newValues = { ...values };

        if (multipleSelection) {
            if (checked) {
                newValues[network.deviceId] = network;
            } else {
                delete newValues[network.deviceId];
            }

            setValues(newValues);
            return;
        }

        setValues({
            [network.deviceId]: network,
        });
    };

    const handleToggleAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        const newValues = { ...values };

        if (checked) {
            // Check max devices limit before adding
            if (
                maxSelected &&
                Object.keys(values).length + filteredList.length > maxSelected
            ) {
                return;
            }

            filteredList.forEach((device) => {
                newValues[device.deviceId] = device;
            });
        } else {
            filteredList.forEach((device) => {
                delete newValues[device.deviceId];
            });
        }

        setValues(newValues);
    };

    const renderEmptyState = () => {
        return loading ? (
            <Spinner animation="border" variant="primary" />
        ) : (
            <p className="w-100 d-flex justify-content-center">
                No {displayedNetworkType} to display
            </p>
        );
    };

    const renderFooter = () => {
        const selectedCount = Object.keys(values).length;
        const countText = maxSelected ? `${selectedCount}/${maxSelected}` : selectedCount;

        return (
            <div className="row w-100 justify-content-between align-items-center">
                <strong className="text-gray">
                    {selectedCount > 0 &&
                        `${countText} ${displayedNetworkType} are selected`}
                </strong>
                <Button
                    variant="primary"
                    onClick={(event) => {
                        onHide?.();
                        onSave?.(event, values);
                    }}
                >
                    ADD
                </Button>
            </div>
        );
    };

    return (
        <StyledDashboardCreateModal
            title={`Select ${displayedNetworkType}`}
            show={show}
            onHide={() => onHide?.()}
            footer={renderFooter}
        >
            {uniqNetworkList?.length > 0 && (
                <Row className="mb-2">
                    <Col sm="12" className="m-0 p-0">
                        <InputGroup className="text-gray flex align-items-center p-3 border rounded bg-light-gray">
                            <span className="mr-3">Filter By:</span>
                            <Form.Control
                                className="rounded"
                                as="select"
                                type="text"
                                placeholder="Search.."
                                size="sm"
                                onChange={(e) =>
                                    setSearchCondition({
                                        ...searchCondition,
                                        networkId: e.target.value,
                                    })
                                }
                                custom
                            >
                                <option key="all" value="">
                                    All Networks
                                </option>
                                {uniqNetworkList.map(
                                    ({
                                        gatewayName: networkName,
                                        gatewayId: networkId,
                                    }) => (
                                        <option
                                            key={networkId}
                                            value={networkId}
                                        >
                                            {networkName}
                                        </option>
                                    )
                                )}
                            </Form.Control>

                            <Form.Control
                                className="rounded ml-3"
                                as="select"
                                type="text"
                                placeholder="Search.."
                                size="sm"
                                defaultValue={networkType || ""}
                                onChange={(e) =>
                                    setSearchCondition({
                                        ...searchCondition,
                                        type: e.target.value,
                                    })
                                }
                                disabled={!!networkType}
                                custom
                            >
                                <option value="">All Types</option>
                                {networkTypeOptions.map(({ value, label }) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </Form.Control>
                        </InputGroup>
                    </Col>
                </Row>
            )}
            <Row className="align-items-center mb-3 modal-filter">
                {multipleSelection && (
                    <Col
                        sm="4"
                        className="modal-option text-gray-important mx-0 p-0"
                    >
                        <Form.Check
                            id="device-check-all"
                            type="checkbox"
                            label="Select All"
                            disabled={
                                maxSelected !== undefined &&
                                filteredList.length > maxSelected
                            }
                            custom
                            checked={isSelectAll}
                            onChange={handleToggleAll}
                        />
                    </Col>
                )}
                <Col
                    sm={multipleSelection ? "8" : "12"}
                    className={multipleSelection ? "pl-lg-3 pr-0" : "p-0"}
                >
                    <Form.Control
                        type="text"
                        placeholder="Search.."
                        onChange={(e) =>
                            setSearchCondition({
                                ...searchCondition,
                                name: e.target.value,
                            })
                        }
                    ></Form.Control>
                </Col>
            </Row>

            <Form className="d-flex flex-wrap modal-options">
                {filteredList.length > 0 && !loading
                    ? filteredList.map((network, index) => (
                        <div
                            className="modal-option"
                            key={network.deviceId + network.gatewayId}
                        >
                            <Form.Check
                                name="selected-device"
                                value={network.deviceId}
                                checked={network.deviceId in values}
                                disabled={
                                    maxSelected !== undefined &&
                                    !values[network.deviceId] &&
                                    Object.keys(values).length >= maxSelected
                                }
                                onChange={(e) => handleSelect(e, network)}
                                id={`${index}-${network.deviceId}-${network.gatewayId}`}
                                custom
                                type={multipleSelection ? "checkbox" : "radio"}
                                label={
                                    <div>
                                        <h4>{network.deviceName}</h4>
                                        <p>{network.gatewayName}</p>
                                        <p>Network ID: {network.gatewayId}</p>
                                        <p>
                                            {network.deviceType
                                                ?.toString()
                                                .capitalize()}
                                        </p>
                                    </div>
                                }
                                className="float-left"
                            />
                        </div>
                    ))
                    : renderEmptyState()}
            </Form>
        </StyledDashboardCreateModal>
    );
};

export default memo(DashboardCreateSelectNetworkModal);
